"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesOptions = ["Strength & Conditioning", "Nutritional Guidance", "Functional Movement Training", "Lifestyle & Habit Coaching"];
const fundingStages = ["Beginner", "Intermediate", "Advanced", "Athlete"];
const budgetRanges = ["$200 – $500/mo", "$500 – $1,000/mo", "$1,000 – $2,000/mo", "$2,000+/mo"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-card">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-3">// APPLICATION RECEIVED</p>
        <p className="font-default text-2xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-xs uppercase tracking-widest">
          Submit Another Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">FULL NAME</FormLabel>
            <FormControl>
              <Input placeholder="John Smith" {...field} className="font-mono text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" rules={{
      required: "Occupation is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">OCCUPATION</FormLabel>
            <FormControl>
              <Input placeholder="Software Engineer" {...field} className="font-mono text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">EMAIL</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="font-mono text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">PHONE</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="font-mono text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="fundingStage" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">FITNESS LEVEL</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="font-mono text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary w-full data-[placeholder]:text-muted-foreground/60">
                  <SelectValue placeholder="Select level" />
                </SelectTrigger>
                <SelectContent>
                  {fundingStages.map((stage, i) => <SelectItem key={stage} value={stage} data-index={i} className="font-mono text-sm">
                      {stage}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="services" rules={{
      required: "Please select at least one focus area"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">
              PRIMARY GOALS
            </FormLabel>
            <FormControl>
              <div className="space-y-3 pt-2">
                {servicesOptions.map((service, i) => {
            const selected: string[] = Array.isArray(field.value) ? field.value : [];
            const isChecked = selected.includes(service);
            return <label data-index={i} key={service} className="flex items-start gap-3 cursor-pointer group">
                      <Checkbox checked={isChecked} onCheckedChange={checked => {
                if (checked) {
                  field.onChange([...selected, service]);
                } else {
                  field.onChange(selected.filter(s => s !== service));
                }
              }} className="mt-0.5" />
                      <span className="font-mono text-sm text-foreground/90 group-hover:text-primary transition-colors">
                        {service}
                      </span>
                    </label>;
          })}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">
              YOUR FITNESS STORY
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your current routine, injuries, and what you want to achieve..." {...field} className="font-mono text-sm border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/60 min-h-[200px] resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="timeline" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">START DATE</FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-6">
                {["ASAP", "Next Month", "Flexible"].map((option, i) => <label data-index={i} key={option} className="flex items-center gap-2 cursor-pointer group">
                    <RadioGroupItem value={option} />
                    <span className="font-mono text-sm text-foreground/90 group-hover:text-primary transition-colors">
                      {option}
                    </span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-primary">MONTHLY BUDGET</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="font-mono text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary w-full data-[placeholder]:text-muted-foreground/60">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  {budgetRanges.map((range, i) => <SelectItem key={range} value={range} data-index={i} className="font-mono text-sm">
                      {range}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4 flex flex-col items-stretch sm:items-end gap-4">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto font-mono text-xs uppercase tracking-widest px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90 transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing...
            </> : <>
              Apply Now
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/80 max-w-sm sm:text-right">
          Applications are reviewed personally by Coach Kira to ensure a perfect fit.
        </p>
      </div>
    </Form>;
}
export default function LetSBuildYourPlan() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const infoBlocks = [{
    label: "Consultation",
    value: "Free"
  }, {
    label: "Commitment",
    value: "3 Months+"
  }, {
    label: "Status",
    value: "Accepting New"
  }];
  return <section data-section-id="3375" id="lets-build-your-plan" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-[180px] px-6 md:px-12">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} aria-hidden="true" />

      <div className="absolute top-1/4 -right-32 w-[600px] h-[600px] pointer-events-none opacity-10" style={{
      background: "radial-gradient(circle, var(--primary) 0%, transparent 70%)",
      filter: "blur(120px)"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 flex flex-col">
            

            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground mb-8">
              Start your <span className="italic font-light">transformation.</span>
            </h2>

            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-md">
              Ready to commit to your health? Apply for 1-on-1 coaching with Coach Kira and let's build your custom roadmap.
            </p>

            <div className="space-y-6 mt-auto pt-8 border-t border-border">
              {infoBlocks.map((block, i) => <div data-index={i} key={block.label} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-4">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {block.label}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-widest text-foreground">
                    {block.value}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="relative bg-card border border-border p-8 md:p-12">
                <div className="flex items-center justify-between mb-10 pb-6 border-b border-border">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Coaching Application
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-primary">
                    KIRA / 2026
                  </span>
                </div>

                <FormProvider formKey="plan_builder_start_your_journey" formId="plan_builder_start_your_journey" sectionName="lets-build-your-plan" defaultValues={{
                name: "",
                company: "",
                email: "",
                phone: "",
                fundingStage: "",
                services: [],
                message: "",
                timeline: "",
                budget: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                company: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                services: {
                  required: true
                }
              }} submitText="Apply Now" successMessage="Application received. Coach Kira will review your details and reach out within 24 hours." errorMessage="Something went wrong. Please try again.">
                  <InquiryForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}